import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ReviewsSection = ({ reviews, overallRating, ratingDistribution }) => {
  const [filter, setFilter] = useState('all');
  const [visibleReviews, setVisibleReviews] = useState(6);

  const filteredReviews = filter === 'all' 
    ? reviews 
    : reviews?.filter(review => review?.rating === parseInt(filter));

  const displayedReviews = filteredReviews?.slice(0, visibleReviews);

  return (
    <div className="bg-card rounded-xl shadow-md border border-border overflow-hidden">
      <div className="p-6 md:p-8 border-b border-border">
        <h2 className="text-2xl font-semibold text-foreground mb-6">Customer Reviews</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="text-center p-6 rounded-lg bg-muted">
              <div className="text-5xl font-bold text-foreground mb-2">{overallRating}</div>
              <div className="flex items-center justify-center gap-1 mb-2">
                {[...Array(5)]?.map((_, i) => (
                  <Icon
                    key={i}
                    name="Star"
                    size={20}
                    className={i < Math.floor(overallRating) ? 'text-accent fill-accent' : 'text-muted'}
                  />
                ))}
              </div>
              <p className="text-sm text-muted-foreground">Based on {reviews?.length} reviews</p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="space-y-3">
              {[5, 4, 3, 2, 1]?.map((rating) => {
                const count = ratingDistribution?.[rating] || 0;
                const percentage = (count / reviews?.length) * 100;
                return (
                  <div key={rating} className="flex items-center gap-3">
                    <div className="flex items-center gap-1 w-20">
                      <span className="text-sm font-medium text-foreground">{rating}</span>
                      <Icon name="Star" size={14} className="text-accent fill-accent" />
                    </div>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-muted-foreground w-12 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 ${
              filter === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            All Reviews
          </button>
          {[5, 4, 3, 2, 1]?.map((rating) => (
            <button
              key={rating}
              onClick={() => setFilter(rating?.toString())}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 ${
                filter === rating?.toString()
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              {rating} Stars
            </button>
          ))}
        </div>
      </div>
      <div className="divide-y divide-border">
        {displayedReviews?.map((review) => (
          <div key={review?.id} className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <Image
                src={review?.avatar}
                alt={review?.avatarAlt}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
                  <div>
                    <h4 className="font-semibold text-foreground">{review?.name}</h4>
                    <p className="text-sm text-muted-foreground caption">{review?.date}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)]?.map((_, i) => (
                      <Icon
                        key={i}
                        name="Star"
                        size={16}
                        className={i < review?.rating ? 'text-accent fill-accent' : 'text-muted'}
                      />
                    ))}
                  </div>
                </div>
                
                {review?.serviceUsed && (
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-muted text-sm text-foreground mb-3">
                    <Icon name="Package" size={14} />
                    <span>{review?.serviceUsed}</span>
                  </div>
                )}

                <p className="text-foreground leading-relaxed mb-3">{review?.comment}</p>

                {review?.images && review?.images?.length > 0 && (
                  <div className="flex gap-2 mb-3 overflow-x-auto">
                    {review?.images?.map((img, index) => (
                      <Image
                        key={index}
                        src={img?.src}
                        alt={img?.alt}
                        className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                      />
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <button className="flex items-center gap-1 hover:text-foreground transition-colors">
                    <Icon name="ThumbsUp" size={16} />
                    <span>Helpful ({review?.helpful})</span>
                  </button>
                  {review?.verified && (
                    <span className="flex items-center gap-1 text-success">
                      <Icon name="CheckCircle" size={16} />
                      <span>Verified Purchase</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredReviews?.length > visibleReviews && (
        <div className="p-6 border-t border-border text-center">
          <Button
            variant="outline"
            onClick={() => setVisibleReviews(prev => prev + 6)}
          >
            Load More Reviews
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;